/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, MenuItem } from '@mui/material';
import type { ElementType, FC } from 'react';
import { useMemo } from 'react';
import { AlertIcon, TooltipSelect } from '@opengeoweb/shared';
import type { mapTypes } from '@opengeoweb/store';
import { layerTypes } from '@opengeoweb/store';
import { useCoreTranslation } from '../../../../../utils/i18n';
import type { DimensionValueMappings } from '../../../LayerManagerUtils';

export enum LatestOptions {
  LATEST_0 = 'Latest',
  LATEST_1 = 'Latest -1',
  LATEST_2 = 'Latest -2',
  LATEST_3 = 'Latest -3',
}

const createLatestMap = (
  dimensionValuesRaw: string[],
): {
  getValue: (value: string) => LatestOptions | string;
  getIndex: (value: string) => number;
  latestOptions: LatestOptions[];
} => {
  const latestOptions = Object.values(LatestOptions).slice(
    0,
    dimensionValuesRaw.length,
  );
  return {
    getValue: (value: string): LatestOptions | string => {
      const index = dimensionValuesRaw.indexOf(value);
      return index !== -1 ? latestOptions[index] : value;
    },
    getIndex: (value: string): number =>
      latestOptions.indexOf(value as LatestOptions),
    latestOptions,
  };
};

const DimensionSelectValue: FC<{
  selectedDimension?: Omit<mapTypes.Dimension, 'currentValue'> & {
    currentValue?: string;
  };
  generatedValues: string[];
  icon?: ElementType;
  tooltip: string;
  isEnabled: boolean;
  onLayerChangeDimension: (
    dimensionName: string,
    dimensionValue: string,
    origin?: layerTypes.LayerActionOrigin.layerManager,
  ) => void;
  valueMappings?: DimensionValueMappings;
  hideLatestDuplicate?: boolean;
  useLatestReferenceTime?: boolean;
  skipLocalStyling?: boolean;
  setUseLatestReferenceTime?: (useLatestReferenceTime: boolean) => void;
}> = ({
  isEnabled,
  tooltip,
  selectedDimension,
  generatedValues = [],
  icon,
  onLayerChangeDimension,
  valueMappings,
  hideLatestDuplicate,
  setUseLatestReferenceTime,
  useLatestReferenceTime,
  skipLocalStyling,
}) => {
  const { t } = useCoreTranslation();

  const dimensionValuesRaw = generatedValues.slice().reverse().slice(0, 100);

  const latestMap = useMemo(
    () => createLatestMap(dimensionValuesRaw),
    [dimensionValuesRaw],
  );

  if (!selectedDimension?.name || !dimensionValuesRaw?.length) {
    return null;
  }

  const isReferenceTimeDimensionAndUseLatestIsAvailable =
    selectedDimension.name === 'reference_time' &&
    useLatestReferenceTime !== undefined;

  const latestValue = dimensionValuesRaw[0];

  const dimensionValues = isReferenceTimeDimensionAndUseLatestIsAvailable
    ? [
        ...latestMap.latestOptions,
        ...dimensionValuesRaw.slice(hideLatestDuplicate ? 1 : 0),
      ]
    : dimensionValuesRaw.slice().reverse();

  const extendedDimensionValues =
    dimensionValues.findIndex(
      (dimensionValue) => dimensionValue === selectedDimension.currentValue,
    ) === -1 && selectedDimension.currentValue
      ? [...dimensionValues, selectedDimension.currentValue]
      : dimensionValues;

  const setDimensionValue = (dimensionValue: string): void => {
    if (isReferenceTimeDimensionAndUseLatestIsAvailable) {
      const index = latestMap.getIndex(dimensionValue);
      if (index !== -1) {
        setUseLatestReferenceTime?.(true);
        onLayerChangeDimension(
          selectedDimension.name!,
          dimensionValuesRaw[index],
          layerTypes.LayerActionOrigin.layerManager,
        );
        return;
      }
      setUseLatestReferenceTime?.(false);
    }
    onLayerChangeDimension(
      selectedDimension.name!,
      dimensionValue,
      layerTypes.LayerActionOrigin.layerManager,
    );
  };

  const isOneOfLatest =
    isReferenceTimeDimensionAndUseLatestIsAvailable && useLatestReferenceTime;

  const getValue = (): string => {
    if (isOneOfLatest) {
      return latestMap.getValue(selectedDimension.currentValue || '') || '';
    }
    return selectedDimension.currentValue || '';
  };

  const getIndex = (): number => {
    if (isOneOfLatest) {
      return latestMap.getIndex(selectedDimension.currentValue || '');
    }
    return extendedDimensionValues.findIndex(
      (dimensionValue) => dimensionValue === selectedDimension.currentValue,
    );
  };

  return (
    <TooltipSelect
      IconComponent={icon}
      SelectDisplayProps={{ 'aria-label': 'dimensionSelectValue' }}
      currentIndex={getIndex()}
      data-testid="dimensionSelectValue"
      disableUnderline
      isEnabled={isEnabled}
      list={extendedDimensionValues.map((dimensionValue) => ({
        value: dimensionValue,
      }))}
      onChange={(event): void => {
        event.stopPropagation();
        setDimensionValue(event.target.value as string);
      }}
      onChangeMouseWheel={(event): void => {
        setDimensionValue(event.value);
      }}
      skipLocalStyling={skipLocalStyling}
      style={{
        maxWidth: '50%',
      }}
      tooltip={tooltip}
      value={getValue()}
    >
      <MenuItem disabled>
        {t('layermanager-dimensions-total-values', {
          total_dimensions: extendedDimensionValues.length,
        })}
      </MenuItem>
      {extendedDimensionValues.map((dimensionValue, currentIndex) => (
        <MenuItem key={dimensionValue} value={dimensionValue}>
          {valueMappings?.[selectedDimension.name!]?.(
            dimensionValue,
            isReferenceTimeDimensionAndUseLatestIsAvailable
              ? latestValue
              : undefined,
          ) || (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                '& .MuiSvgIcon-root': {
                  fill: 'currentcolor!important',
                },
              }}
            >
              <span
                style={{
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
              >
                {dimensionValue}
                {!dimensionValue?.includes('Latest') && (
                  <i> {selectedDimension.units}</i>
                )}
              </span>
              {currentIndex === dimensionValues.length && (
                <AlertIcon severity="error" />
              )}
            </Box>
          )}
        </MenuItem>
      ))}
    </TooltipSelect>
  );
};

export default DimensionSelectValue;
